import { useEffect, useRef } from "react";
import { Phone, MessageCircle, CheckCircle2, Clock3 } from "lucide-react";
import { SITE } from "../data/site";
import { trackMetaCustom } from "../lib/metaPixel";

const PHONE = "0221 67029067";
const PHONE_HREF = "tel:+4922167029067";
const WA_UNFALL = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hallo, ich hatte gerade einen Unfall und brauche Hilfe.")}`;

const isBeratungszeit = () => {
  const now = new Date();
  const day = now.getDay(); // 0=So, 6=Sa
  const hour = now.getHours();
  return day >= 1 && day <= 5 && hour >= 9 && hour < 22;
};

const trackCall = () => trackMetaCustom("click_call_unfallort");
const trackWa = () => trackMetaCustom("click_whatsapp_unfallort");

// Statuszeile wird erst nach dem Mount per DOM gesetzt (mit
// suppressHydrationWarning) – zeitabhängige Inhalte dürfen das von React
// hydrierte Markup nicht verändern, sonst brechen Buttons (Hydration-Mismatch).
const StatusZeile = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.textContent = isBeratungszeit()
        ? "Jetzt erreichbar – wir gehen sofort ran."
        : "Außerhalb der Beratungszeit? WhatsApp schreiben – wir melden uns schnellstmöglich.";
    }
  }, []);
  return <p ref={ref} suppressHydrationWarning className="mt-2 text-xs font-semibold text-emerald-600 min-h-[1rem]" data-testid="soforthilfe-status" />;
};

const punkte = [
  "Unfallstelle sichern – was ist jetzt wichtig?",
  "Polizei rufen – ja oder nein?",
  "Welche Fotos und Daten Sie unbedingt brauchen",
  "Was Sie unterschreiben sollten – und was nicht",
  "Wie es mit Ihrem Auto weitergeht (fahrbereit, abschleppen, Werkstatt)",
  "Wie Sie an Ihr Geld kommen – ohne Ärger mit der Versicherung",
];

export const SofortHilfeInner = () => {
  return (
      <div className="relative rounded-3xl overflow-hidden bg-signal text-white shadow-2xl grain" data-testid="sofort-hilfe-inner">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
        <div className="relative z-10 p-6 sm:p-10 grid lg:grid-cols-[1.2fr_1fr] gap-8">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] bg-white/15 rounded-full px-4 py-1.5"><Phone size={13} /> Wir beraten Sie noch am Unfallort</p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold mt-4 leading-tight">Sie hatten gerade einen Unfall?</h2>
            <p className="mt-3 text-white/95 text-base md:text-lg font-medium">Wir sagen Ihnen sofort am Telefon, was jetzt zu tun ist – noch direkt am Unfallort.</p>
            <p className="mt-4 text-white/90 text-sm leading-relaxed max-w-xl">
              Ein Unfall ist ein Schock. Viele machen in den ersten Minuten Fehler, die später Geld kosten: falsche Aussagen, fehlende Fotos, ein vorschnell unterschriebenes Schuldanerkenntnis oder der Gutachter der gegnerischen Versicherung.
            </p>
            <p className="mt-3 text-white/90 text-sm leading-relaxed max-w-xl">
              Rufen Sie uns an, solange Sie noch vor Ort sind. Unsere TÜV-zertifizierten KFZ-Sachverständigen gehen mit Ihnen Schritt für Schritt durch:
            </p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2">
              {punkte.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-white/95"><CheckCircle2 size={16} className="shrink-0 mt-0.5" /> {p}</li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col justify-center">
            <div className="bg-white text-navy rounded-2xl p-6 shadow-xl">
              <p className="text-xs font-bold uppercase tracking-wider text-signal flex items-center gap-1.5"><Clock3 size={13} /> Telefonische Erstberatung: kostenlos</p>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">Wenn Sie nicht schuld sind, ist auch Ihr Gutachten für Sie kostenlos. Die gegnerische Versicherung zahlt.</p>
              <a href={PHONE_HREF} onClick={trackCall} data-testid="soforthilfe-call-button" className="mt-5 cta-gradient flex items-center justify-center gap-2 rounded-xl px-6 py-4 font-bold text-white text-lg transition-transform hover:scale-[1.02]">
                <Phone size={20} /> Jetzt anrufen: {PHONE}
              </a>
              <a href={WA_UNFALL} onClick={trackWa} target="_blank" rel="noopener noreferrer" data-testid="soforthilfe-whatsapp-button" className="mt-3 flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-bold bg-emerald-500 hover:bg-emerald-400 text-white transition-colors">
                <MessageCircle size={18} /> WhatsApp: Fotos senden
              </a>
              <StatusZeile />
              <p className="mt-4 text-[11px] text-slate-500 text-center leading-snug">Mo–Fr 9–22 Uhr · Sa/So nach Vereinbarung · Köln, Umland, Krefeld, Moers, Duisburg, Essen</p>
            </div>
          </div>
        </div>
      </div>
  );
};

export const SofortHilfeBox = () => (
  <section data-testid="sofort-hilfe-box" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
    <SofortHilfeInner />
  </section>
);

// Kompakte CTA-Version für Ratgeber-Artikel
export const SofortHilfeCta = () => (
  <div data-testid="sofort-hilfe-cta" className="my-8 bg-signal text-white rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 shadow-lg not-prose">
    <div className="flex-1">
      <p className="font-display text-xl font-bold leading-snug">Sie stehen gerade am Unfallort? Rufen Sie uns an.</p>
      <p className="text-sm text-white/90 mt-1">Wir sagen Ihnen in wenigen Minuten, was jetzt wichtig ist. Kostenlos und unverbindlich.</p>
    </div>
    <div className="flex flex-col gap-2 shrink-0">
      <a href={PHONE_HREF} onClick={trackCall} data-testid="soforthilfe-cta-call" className="inline-flex items-center justify-center gap-2 bg-white text-signal font-bold px-5 py-2.5 rounded-full hover:bg-slate-100 transition-colors whitespace-nowrap"><Phone size={16} /> {PHONE}</a>
      <a href={WA_UNFALL} onClick={trackWa} target="_blank" rel="noopener noreferrer" data-testid="soforthilfe-cta-whatsapp" className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-white font-bold px-5 py-2.5 rounded-full hover:bg-emerald-400 transition-colors whitespace-nowrap"><MessageCircle size={16} /> WhatsApp</a>
    </div>
  </div>
);
